import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import api from './apiService';
import { SoarApp } from './routePaths';
import { SoarStoreProvider } from './SoarStoreProvider';

/**
 * Sync component to bridge React context with the apiService.
 * This is necessary for non-React code like static services.
 */
const SoarStoreSync = () => {
  const { api: xhrFunc } = useAvContext();
  api.syncXhrFunc(xhrFunc);
  return null;
};

/**
 * Portable user interface that can be extracted to an external library.
 */
export interface SoarUser {
  userId: string;
  userName: string;
  email: string;
  permissions: any; // Raw permissions data
  featureFlags: Record<string, boolean>;
  [key: string]: any;
}

/**
 * The state provided by the SoarContext.
 */
export interface SoarContextState {
  accountId: string;
  accessToken?: string; // Needed for API calls from the external app
  user: SoarUser | null;
  /**
   * Callback to trigger a token refresh in the host application.
   * Returns a promise that resolves when the refresh is complete.
   */
  onRefresh: () => Promise<void | string>;
  /**
   * The host app rendering the SOAR views. Drives navigation targets so the
   * same views work under both the threats and identities route trees.
   */
  soarApp: SoarApp;
  /**
   * Optional allow-list of entity types (upper-cased) the views should be
   * limited to. Undefined means no scoping (default threats behaviour).
   */
  entityScope?: string[];
}

const SoarContext = createContext<SoarContextState | undefined>(undefined);

/**
 * Core SoarProvider designed for reusability.
 */
export const SoarProvider = ({
  children,
  accountId,
  accessToken,
  user,
  onRefresh,
  soarApp = 'threats',
  entityScope,
}: {
  children: ReactNode;
  accountId: string;
  accessToken?: string;
  user: SoarUser | null;
  onRefresh: () => Promise<void | string>;
  soarApp?: SoarApp;
  entityScope?: string[];
}) => {
  const value = useMemo(
    () => ({ accountId, accessToken, user, onRefresh, soarApp, entityScope }),
    [accountId, accessToken, user, onRefresh, soarApp, entityScope]
  );
  return (
    <SoarContext.Provider value={value}>
      <SoarStoreProvider entityScope={entityScope}>
        <SoarStoreSync />
        {children}
      </SoarStoreProvider>
    </SoarContext.Provider>
  );
};

/**
 * Custom hook to access soar context data.
 */
export const useSoarContext = () => {
  const context = useContext(SoarContext);
  if (context === undefined) {
    throw new Error('useSoarContext must be used within a SoarProvider');
  }
  return context;
};
